'use client'

import { Button } from '@/components/ui/button'
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel'
import { msg } from '@lingui/core/macro'
import { format, parseISO } from 'date-fns'
import { motion } from 'framer-motion'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { SiteHeadingV2 } from '../../fragments/site-heading-v2'
import { useLingui } from '@lingui/react'
import { NewsService } from '@/lib/api'
import { NewsItem } from '@/types/response-api'

const IMAGE_BASE = process.env.NEXT_PUBLIC_API_URL_IMAGE || ''

export default function NewsEventSection() {
  const { i18n } = useLingui()
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [allPosts, setAllPosts] = useState<NewsItem[]>([])

  useEffect(() => {
    if (!carouselApi) return
    const updateSelection = () => {
      setCanScrollPrev(carouselApi.canScrollPrev())
      setCanScrollNext(carouselApi.canScrollNext())
      if (!isInitialized) setIsInitialized(true)
    }
    updateSelection()
    carouselApi.on('select', updateSelection)
    return () => {
      carouselApi.off('select', updateSelection)
    }
  }, [carouselApi, isInitialized])

  useEffect(() => {
    const getNews = async () => {
      try {
        const res = await NewsService.fetchNews({ limit: 10, isActive: true })
        if (res.data?.status && res.data?.data?.rows) {
          setAllPosts(res.data.data.rows)
        }
      } catch (error) {
        console.error('Error fetching news:', error)
      }
    }
    getNews()
  }, [])

  const isDisabled = !isInitialized || !canScrollPrev
  const isNextDisabled = !isInitialized || !canScrollNext

  return (
    <section className="padding-section flex items-center bg-white min-h-svh h-full bg-[url('/landing/news-event/bg-news-event.png')] bg-cover bg-no-repeat bg-center relative overflow-hidden">
      <div className='container relative flex flex-col justify-center h-full'>
        <SiteHeadingV2
          title={msg`News & Event`}
          description={msg`Comprehensive technology solutions for businesses in the digital era`}
          className='max-w-3xl text-left'
          classNameWrapper='items-start'
        />
        <div className='z-10 flex items-center justify-end w-full gap-2'>
          <Button
            size={'icon'}
            variant={'outline'}
            onClick={() => carouselApi?.scrollPrev()}
            disabled={isDisabled}
            className='z-10 rounded-full border-0 p-3 bg-[#031A36]'
          >
            <ArrowLeft className='size-6 text-white/70' />
          </Button>
          <Button
            size={'icon'}
            variant={'outline'}
            onClick={() => carouselApi?.scrollNext()}
            disabled={isNextDisabled}
            className='rounded-full border-0 p-3 bg-[#031A36]'
          >
            <ArrowRight className='size-6 text-white/70' />
          </Button>
        </div>
        <div className='relative min-h-[50vh]'>
          <div className='absolute left-0 w-screen z-10 translate-x-[calc((100vw - 100%) / -2)]'>
            <Carousel
              setApi={setCarouselApi}
              opts={{
                breakpoints: {
                  '(max-width: 768px)': { dragFree: true }
                }
              }}
            >
              <CarouselContent className=''>
                {allPosts.map((item) => (
                  <CarouselItem
                    key={item.id}
                    className='max-w-[320px] relative pl-6 py-4 lg:max-w-[390px] w-full select-none group'
                  >
                    <motion.div className='relative rounded-[30px] overflow-hidden h-full min-h-[330px] sm:min-h-[390px] flex items-center'>
                      <Image
                        src={
                          item.banner ?
                            `${IMAGE_BASE.replace(/\/$/, '')}${item.banner.startsWith('/') ? '' : '/'}${item.banner}`
                          : '/landing/images/placeholder-news.png'
                        }
                        alt={item.title}
                        width={398}
                        height={404}
                        loading='lazy'
                        className='w-[99%] h-[99%] rounded-[31px] object-cover m-auto'
                        unoptimized={item.banner?.startsWith('http')}
                      />
                      <div className='absolute inset-0 flex h-full w-full flex-col justify-between p-5 md:p-6 overflow-hidden transition-colors duration-300 bg-gradient-to-t from-[rgba(2,58,71,0.8)]/90 to-[#001341]/90 group-hover:from-[rgba(0,208,255,0.9)]/90 group-hover:to-[#001341]/90'>
                        <div className='flex flex-col items-start justify-start gap-2 min-h-64'>
                          <p className='text-sm sm:text-base text-[#2BD0C5]'>
                            {format(parseISO(item.createdAt), 'MMMM d, yyyy')}
                          </p>
                          <Link href={`/news-events/${item.slug}`}>
                            <h3 className='text-lg font-semibold text-white sm:text-xl line-clamp-4 xl:text-2xl font-jost'>
                              {item.title}
                            </h3>
                          </Link>
                          <p className='text-sm text-white/90 line-clamp-2'>{item.brief}</p>
                        </div>
                        <Link
                          href={`/news-events/${item.slug}`}
                          className='text-white text-sm z-10 p-2 px-5 rounded-full w-fit transition-colors duration-300 bg-gradient-to-l from-[#053C68] to-[#007C91] hover:from-[#35CCBA] hover:to-[#EF830B]'
                        >
                          {i18n._('Read more')} →
                        </Link>
                      </div>
                    </motion.div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        </div>
      </div>
    </section>
  )
}
